import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./SpotifyArtistsDiscovery.css";

// This page's own serverless functions live in netlify/functions and are
// mounted at this same path via the redirects in the root netlify.toml —
// see netlify/functions/_lib/session.js (BASE_PATH) for the backend half.
const BASE = "/spotify-artists-discovery";
const MAX_ARTISTS = 50;
const MAIN_SITE_TITLE = "Janos Hrubos | Developer";

export const SpotifyArtistsDiscovery = () => {
  const [searchParams] = useSearchParams();

  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState([]);
  const [playlistName, setPlaylistName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Artist Discovery for Spotify";
    return () => {
      document.title = MAIN_SITE_TITLE;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE}/api/me`)
      .then((res) => res.json())
      .catch(() => ({ authenticated: false }))
      .then((data) => {
        if (cancelled) return;
        setUser(data.authenticated ? data.user : null);
        setAuthChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${BASE}/api/search-artists?${new URLSearchParams({ q })}`);
        const data = await res.json();
        setResults(data.artists || []);
        setShowResults(true);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const authState = searchParams.get("auth");
  const authNotice =
    !user && authState
      ? authState === "denied"
        ? "Spotify login was cancelled."
        : "Something went wrong connecting to Spotify — please try again."
      : null;

  function addArtist(artist) {
    if (selected.length >= MAX_ARTISTS || selected.some((a) => a.id === artist.id)) return;
    setSelected([...selected, artist]);
    setQuery("");
    setShowResults(false);
  }

  function removeArtist(id) {
    setSelected(selected.filter((a) => a.id !== id));
  }

  async function handleGenerate() {
    if (!selected.length) return;
    setGenerating(true);
    setGenerateError(null);
    setResult(null);
    try {
      const res = await fetch(`${BASE}/api/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artistIds: selected.map((a) => a.id),
          playlistName: playlistName.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setGenerateError(data.error || "Something went wrong generating the playlist.");
        return;
      }
      setResult(data);
    } catch {
      setGenerateError("Network error — please try again.");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="spotify-artists-discovery">
      <div className="bg-glow" aria-hidden="true" />

      <header className="topbar">
        <div className="brand">
          <svg className="brand-mark" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
            <circle cx="12" cy="12" r="12" fill="#1ED760" />
            <path d="M6.5 9.5c3.6-1.1 8-.9 10.9.9" stroke="#121212" strokeWidth="1.6" strokeLinecap="round" fill="none" />
            <path d="M6.9 12.6c3-.9 6.7-.7 9.2.8" stroke="#121212" strokeWidth="1.6" strokeLinecap="round" fill="none" />
            <path d="M7.3 15.6c2.4-.7 5.4-.6 7.4.6" stroke="#121212" strokeWidth="1.4" strokeLinecap="round" fill="none" />
          </svg>
          <span>Artist Discovery</span>
        </div>
        <div className="account">
          {user && (
            <>
              {user.image && <img src={user.image} alt="" />}
              <span>{user.name}</span>
              <a href={`${BASE}/logout`}>Log out</a>
            </>
          )}
        </div>
      </header>

      <main className="page">
        {!authChecked ? null : !user ? (
          <section className="hero">
            <h1>
              Turn your favorite artists
              <br />
              into one playlist.
            </h1>
            <p className="sub">
              Connect your Spotify account, pick up to 50 artists, and we'll build a playlist with each
              artist's top tracks — straight into your library.
            </p>
            <a className="btn btn-spotify" href={`${BASE}/login`}>
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <circle cx="12" cy="12" r="12" fill="#121212" />
                <path d="M6.5 9.5c3.6-1.1 8-.9 10.9.9" stroke="#1ED760" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                <path d="M6.9 12.6c3-.9 6.7-.7 9.2.8" stroke="#1ED760" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                <path d="M7.3 15.6c2.4-.7 5.4-.6 7.4.6" stroke="#1ED760" strokeWidth="1.4" strokeLinecap="round" fill="none" />
              </svg>
              Connect with Spotify
            </a>
            {authNotice && <p className="notice error">{authNotice}</p>}
          </section>
        ) : (
          <section className="app">
            <div className="panel search-panel">
              <label htmlFor="artistSearch" className="panel-label">
                Search artists
              </label>
              <div className="search-box">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="search-icon">
                  <circle cx="11" cy="11" r="7" fill="none" stroke="#b3b3b3" strokeWidth="2" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#b3b3b3" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  id="artistSearch"
                  className="artist-search"
                  type="text"
                  placeholder="e.g. Radiohead, Tame Impala, Daft Punk…"
                  autoComplete="off"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => results.length && setShowResults(true)}
                  onBlur={() => setTimeout(() => setShowResults(false), 150)}
                />
              </div>
              {showResults && (
                <ul className="search-results">
                  {results.length === 0 && !query.trim() ? null : results.length === 0 ? (
                    <li className="empty-hint">No artists found.</li>
                  ) : (
                    results.map((artist) => {
                      const already = selected.some((a) => a.id === artist.id);
                      return (
                        <li
                          key={artist.id}
                          className={already ? "disabled" : ""}
                          onClick={already ? undefined : () => addArtist(artist)}
                        >
                          <img className="result-avatar" src={artist.image || ""} alt="" style={!artist.image ? { visibility: "hidden" } : undefined} />
                          <div className="result-info">
                            <div className="result-name">{artist.name}</div>
                            <div className="result-meta">
                              {already ? "Already selected" : artist.genres.join(", ") || "Artist"}
                            </div>
                          </div>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>

            <div className="panel">
              <div className="panel-label-row">
                <span className="panel-label">Selected artists</span>
                <span className="count-pill">
                  {selected.length} / {MAX_ARTISTS}
                </span>
              </div>
              <ul className="selected-list">
                {selected.length === 0 ? (
                  <li className="empty-hint">No artists yet — search above to add some.</li>
                ) : (
                  selected.map((artist) => (
                    <li key={artist.id}>
                      <img className="chip-avatar" src={artist.image || ""} alt="" style={!artist.image ? { visibility: "hidden" } : undefined} />
                      <span className="chip-name">{artist.name}</span>
                      <button
                        className="chip-remove"
                        aria-label={`Remove ${artist.name}`}
                        onClick={() => removeArtist(artist.id)}
                      >
                        ×
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="panel generate-panel">
              <label htmlFor="playlistName" className="panel-label">
                Playlist name (optional)
              </label>
              <input
                id="playlistName"
                type="text"
                className="text-input"
                placeholder="Artist Discovery Mix"
                value={playlistName}
                onChange={(e) => setPlaylistName(e.target.value)}
              />
              <button className="btn btn-spotify wide" disabled={selected.length === 0 || generating} onClick={handleGenerate}>
                Generate playlist
              </button>
              {generating && (
                <p className="notice">
                  <span className="spinner" /> Generating your playlist — this can take a little while for a lot of
                  artists…
                </p>
              )}
              {!generating && generateError && <p className="notice error">{generateError}</p>}
            </div>

            {result && (
              <div className="panel result-panel">
                <div className="result-title">{result.playlist.name}</div>
                <div className="result-sub">
                  {result.trackCount} tracks from {result.perArtist.length} artist(s)
                  {result.skipped.length ? ` — ${result.skipped.length} artist(s) could not be added` : ""}
                </div>
                <a className="btn btn-spotify" href={result.playlist.url} target="_blank" rel="noopener noreferrer">
                  Open in Spotify
                </a>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};
